import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { PageEvent } from '@angular/material/paginator';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { AuthorizedApiService } from '@core';
import { AuthorizationService } from '@core/service/authorization.service';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import {
  ColumnDefinition,
  MasterTableComponent,
  TableAction,
  TableFilterDefinition,
} from '@shared/components/master-table/master-table.component';
import { ConfirmDeleteDialogComponent } from '@shared/components/confirm-delete-dialog/confirm-delete-dialog.component';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { buildOrgUnitTreeOptions } from '@shared/utils/org-unit-tree.util';
import { CompaniesRecord } from '../../organization/companies-records/companies-record.interface';
import { EmployeeRecord } from '../../organization/employees/employee.interface';
import { OvertimeListResponse, OvertimeRecord, OvertimeResolutionPayload } from './overtime.interface';
import { OvertimeResolutionDialogComponent } from './overtime-resolution-dialog.component';

interface ApiListResponse<T> {
  data?: T[];
  items?: T[];
  results?: T[];
}

interface OrgUnitRecord {
  orgUnitId: string;
  name: string;
  parentId?: string | null;
  type?: string | null;
  orgPath?: string | null;
  isActive?: boolean;
  children?: OrgUnitRecord[];
}

@Component({
  selector: 'app-overtime',
  standalone: true,
  imports: [BreadcrumbComponent, MasterTableComponent, TranslateModule],
  templateUrl: './overtime.component.html',
  styleUrl: './overtime.component.scss',
})
export class OvertimeComponent implements OnInit {
  dataSource = new MatTableDataSource<OvertimeRecord>([]);
  companies: CompaniesRecord[] = [];
  orgUnits: OrgUnitRecord[] = [];
  employees: EmployeeRecord[] = [];
  isLoading = true;
  actions: TableAction<OvertimeRecord>[] = [];
  columnDefinitions: ColumnDefinition[] = [];
  tableFilters: TableFilterDefinition<OvertimeRecord>[] = [];
  activeFilters: Record<string, string | number | boolean | null> = {};
  currentPage = 1;
  pageSize = 15;
  totalItems = 0;
  private loadRequestSequence = 0;

  readonly statusOptions = ['pending', 'approved', 'rejected'] as const;

  private readonly apiService = inject(AuthorizedApiService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private employeeSearchDebounceHandle: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    this.configureTranslations();
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.configureTranslations();
      this.tableFilters = this.buildTableFilters();
      this.dataSource.data = this.dataSource.data.map((item) => this.normalizeOvertime(item));
    });
    void this.initializeData();
  }

  async initializeData(): Promise<void> {
    this.isLoading = true;
    try {
      await Promise.all([this.loadCompanies(), this.loadOrgUnits(), this.loadEmployees()]);
      this.tableFilters = this.buildTableFilters();
      await this.loadData();
    } finally {
      this.isLoading = false;
    }
  }

  async loadData(): Promise<void> {
    const requestId = ++this.loadRequestSequence;
    this.isLoading = true;
    try {
      const response = await firstValueFrom(
        this.apiService.get<OvertimeListResponse>('overtimes', {
          params: {
            page: this.currentPage,
            perPage: this.pageSize,
            sort: '-workDate',
            ...(this.getActiveCompanyId() ? { companiesRecordId: this.getActiveCompanyId() } : {}),
            ...(this.getActiveOrgUnitId() ? { orgUnitId: this.getActiveOrgUnitId() } : {}),
            ...(this.getActiveEmployeeId() ? { employeeId: this.getActiveEmployeeId() } : {}),
            ...(this.getActiveWorkDate() ? { workDate: this.getActiveWorkDate() } : {}),
            ...(this.getActiveStatus() ? { status: this.getActiveStatus() } : {}),
          },
        })
      );

      if (requestId !== this.loadRequestSequence) return;

      this.totalItems = response?.pagination?.total || 0;
      this.dataSource.data = (response?.data || []).map((item) => this.normalizeOvertime(item));
      this.tableFilters = this.buildTableFilters();
    } catch (error) {
      if (requestId !== this.loadRequestSequence) return;
      console.error('Error loading overtimes', error);
      this.dataSource.data = [];
      this.totalItems = 0;
      this.showNotification('snackbar-danger', extractApiErrorMessage(error, this.t('overtimesCatalog.notifications.loadError')), 'bottom', 'center');
    } finally {
      if (requestId === this.loadRequestSequence) this.isLoading = false;
    }
  }

  async handleView(row: OvertimeRecord): Promise<void> {
    if (!this.hasAccess('read')) {
      return;
    }
    await this.router.navigate(['/attendance/overtimes', row.overtimeId], { queryParamsHandling: 'preserve' });
  }

  async onFiltersChange(filters: Record<string, string | number | boolean | null>): Promise<void> {
    const previousCompanyId = this.getActiveCompanyId();
    const previousOrgUnitId = this.getActiveOrgUnitId();
    this.activeFilters = { ...filters };

    if (previousCompanyId !== this.getActiveCompanyId()) {
      this.activeFilters['orgUnit'] = null;
      this.activeFilters['employee'] = null;
      await Promise.all([this.loadOrgUnits(), this.loadEmployees()]);
    } else if (previousOrgUnitId !== this.getActiveOrgUnitId()) {
      this.activeFilters['employee'] = null;
      await this.loadEmployees();
    }

    this.currentPage = 1;
    this.tableFilters = this.buildTableFilters();
    await this.loadData();
  }

  onFilterSearchChange(event: { filterId: string; search: string }): void {
    if (event.filterId !== 'employee') {
      return;
    }

    if (this.employeeSearchDebounceHandle) {
      clearTimeout(this.employeeSearchDebounceHandle);
    }

    this.employeeSearchDebounceHandle = setTimeout(() => {
      void this.loadEmployees(event.search);
    }, 250);
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    void this.loadData();
  }

  async handleActionTriggered(event: { action: TableAction<OvertimeRecord>; selectedRows: OvertimeRecord[] }): Promise<void> {
    const [row] = event.selectedRows;
    if (!row) {
      return;
    }

    if (event.action.id === 'details') {
      await this.handleView(row);
      return;
    }

    if (event.action.id === 'approve') {
      await this.resolveOvertime(row, 'approve');
      return;
    }

    if (event.action.id === 'reject') {
      await this.resolveOvertime(row, 'reject');
      return;
    }

    if (event.action.id === 'delete') {
      await this.deleteOvertimes(event.selectedRows);
    }
  }

  private async resolveOvertime(row: OvertimeRecord, mode: 'approve' | 'reject'): Promise<void> {
    if (!this.hasAccess('approve')) {
      return;
    }

    const payload = await firstValueFrom(
      this.dialog.open(OvertimeResolutionDialogComponent, {
        width: '460px',
        data: { mode, detectedMinutes: row.detectedMinutes },
      }).afterClosed()
    );

    if (!payload) {
      return;
    }

    try {
      await firstValueFrom(this.apiService.post<OvertimeRecord>(`overtimes/${row.overtimeId}/${mode}`, payload as OvertimeResolutionPayload));
      const notificationKey = mode === 'approve' ? 'approved' : 'rejected';
      this.showNotification('snackbar-success', this.t(`overtimesCatalog.notifications.${notificationKey}`), 'bottom', 'center');
      await this.loadData();
    } catch (error) {
      console.error(`Error resolving overtime ${mode}`, error);
      this.showNotification('snackbar-danger', extractApiErrorMessage(error, this.t('overtimesCatalog.notifications.resolveError')), 'bottom', 'center');
    }
  }

  private async deleteOvertimes(rows: OvertimeRecord[]): Promise<void> {
    if (!rows.length || !this.hasAccess('delete')) {
      return;
    }

    const confirmed = await firstValueFrom(
      this.dialog.open(ConfirmDeleteDialogComponent, {
        width: '420px',
        data: {
          title: this.t('overtimesCatalog.dialog.deleteTitle'),
          message: this.t('overtimesCatalog.dialog.deleteMessage', { count: rows.length }),
          confirmText: this.t('overtimesCatalog.dialog.deleteConfirm'),
        },
      }).afterClosed()
    );

    if (!confirmed) {
      return;
    }

    try {
      await Promise.all(rows.map((row) => firstValueFrom(this.apiService.delete(`overtimes/${row.overtimeId}`))));
      this.showNotification('snackbar-success', this.t('overtimesCatalog.notifications.deleted'), 'bottom', 'center');
      await this.loadData();
    } catch (error) {
      console.error('Error deleting overtimes', error);
      this.showNotification('snackbar-danger', extractApiErrorMessage(error, this.t('overtimesCatalog.notifications.deleteError')), 'bottom', 'center');
    }
  }

  private async loadCompanies(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.apiService.get<CompaniesRecord[] | ApiListResponse<CompaniesRecord>>('companies-records', {
          params: { isActive: 'true' },
        })
      );
      this.companies = this.extractList(response).filter((company) => company.isActive !== false);
    } catch (error) {
      console.error('Error loading companies for overtimes', error);
      this.companies = [];
    }
  }

  private async loadOrgUnits(search = ''): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.apiService.get<OrgUnitRecord[] | ApiListResponse<OrgUnitRecord>>('org-units', {
          params: {
            page: 1,
            perPage: 200,
            isActive: 'true',
            ...(this.getActiveCompanyId() ? { companiesRecordId: this.getActiveCompanyId() } : {}),
            ...(search ? { search } : {}),
          },
        })
      );
      this.orgUnits = this.flattenOrgUnits(this.extractList(response)).filter((unit) => unit.isActive !== false);
    } catch (error) {
      console.error('Error loading org units for overtimes', error);
      this.orgUnits = [];
    }
  }

  private async loadEmployees(search = ''): Promise<void> {
    try {
      const selectedEmployeeId = this.getActiveEmployeeId();
      const selectedEmployee = this.employees.find((employee) => employee.employeeId === selectedEmployeeId);
      const response = await firstValueFrom(
        this.apiService.get<EmployeeRecord[] | ApiListResponse<EmployeeRecord>>('employees', {
          params: {
            page: 1,
            perPage: 20,
            isActive: 'true',
            ...(this.getActiveCompanyId() ? { companiesRecordId: this.getActiveCompanyId() } : {}),
            ...(this.getActiveOrgUnitId() ? { orgUnitId: this.getActiveOrgUnitId() } : {}),
            ...(search ? { search } : {}),
          },
        })
      );
      const nextEmployees = this.extractList(response).filter((employee) => employee.isActive !== false);
      this.employees = selectedEmployee && !nextEmployees.some((employee) => employee.employeeId === selectedEmployee.employeeId)
        ? [selectedEmployee, ...nextEmployees]
        : nextEmployees;
      this.tableFilters = this.buildTableFilters();
    } catch (error) {
      console.error('Error loading employees for overtimes', error);
      this.employees = [];
    }
  }

  private normalizeOvertime(item: Partial<OvertimeRecord>): OvertimeRecord {
    const employee = this.employees.find((record) => record.employeeId === item.employeeId);
    const employeeLabel = item.employeeName || employee?.fullName || item.employeeNumber || item.employeeId || '-';
    const status = item.status || 'pending';

    return {
      overtimeId: item.overtimeId || `${item.employeeId || 'overtime'}-${item.workDate || 'row'}`,
      center: item.center || '',
      employeeId: item.employeeId || null,
      employeeNumber: item.employeeNumber || employee?.employeeNumber || '-',
      employeeName: item.employeeName || employee?.fullName || null,
      companiesRecordId: item.companiesRecordId || null,
      orgUnitId: item.orgUnitId || null,
      orgPath: item.orgPath || null,
      workDate: item.workDate || null,
      attendanceResultId: item.attendanceResultId || null,
      detectedMinutes: item.detectedMinutes ?? null,
      approvedMinutes: item.approvedMinutes ?? null,
      status,
      requestedByUid: item.requestedByUid || null,
      requestedAt: item.requestedAt || null,
      resolvedByUid: item.resolvedByUid || null,
      resolvedAt: item.resolvedAt || null,
      resolutionComment: item.resolutionComment || null,
      history: item.history || [],
      isActive: item.isActive !== false,
      createdAt: item.createdAt || null,
      updatedAt: item.updatedAt || null,
      employeeLabel,
      companyName: this.getCompanyName(item.companiesRecordId),
      orgUnitName: this.getOrgUnitName(item.orgUnitId),
      workDateLabel: this.formatDate(item.workDate),
      detectedMinutesLabel: this.formatMinutes(item.detectedMinutes),
      approvedMinutesLabel: this.formatMinutes(item.approvedMinutes),
      statusLabel: this.translateStatus(status),
      requestedAtLabel: this.formatDateTime(item.requestedAt),
      resolvedAtLabel: this.formatDateTime(item.resolvedAt),
      createdAtLabel: this.formatDateTime(item.createdAt),
      updatedAtLabel: this.formatDateTime(item.updatedAt),
      activeLabel: item.isActive !== false ? this.t('common.status.active') : this.t('common.status.inactive'),
    };
  }

  private buildTableFilters(): TableFilterDefinition<OvertimeRecord>[] {
    return [
      {
        id: 'company',
        label: this.t('overtimesCatalog.filters.company'),
        placeholder: this.t('overtimesCatalog.filters.companyPlaceholder'),
        icon: 'business',
        searchable: true,
        value: this.activeFilters['company'] ?? null,
        options: this.companies.map((company) => ({ value: company.companiesRecordId, label: company.name || company.code, searchText: `${company.name || ''} ${company.code || ''}` })),
      },
      {
        id: 'orgUnit',
        label: this.t('overtimesCatalog.filters.orgUnit'),
        placeholder: this.t('overtimesCatalog.filters.orgUnitPlaceholder'),
        icon: 'account_tree',
        type: 'tree-select',
        multiple: false,
        searchPlaceholder: this.t('overtimesCatalog.filters.orgUnitPlaceholder'),
        value: this.activeFilters['orgUnit'] ?? null,
        treeOptions: buildOrgUnitTreeOptions(this.orgUnits),
      },
      {
        id: 'employee',
        label: this.t('overtimesCatalog.filters.employee'),
        placeholder: this.t('overtimesCatalog.filters.employeePlaceholder'),
        icon: 'badge',
        searchable: true,
        value: this.activeFilters['employee'] ?? null,
        options: this.employees.map((employee) => ({ value: employee.employeeId, label: `${employee.fullName || employee.employeeNumber} · ${employee.employeeNumber}`, searchText: `${employee.fullName || ''} ${employee.employeeNumber || ''}`.trim() })),
      },
      {
        id: 'workDate',
        label: this.t('overtimesCatalog.filters.workDate'),
        placeholder: this.t('overtimesCatalog.filters.workDatePlaceholder'),
        icon: 'calendar_today',
        type: 'date',
        value: this.activeFilters['workDate'] ?? null,
      },
      {
        id: 'status',
        label: this.t('overtimesCatalog.filters.status'),
        placeholder: this.t('overtimesCatalog.filters.statusPlaceholder'),
        icon: 'task_alt',
        value: this.activeFilters['status'] ?? null,
        options: this.statusOptions.map((value) => ({ value, label: this.t(`overtimesCatalog.statuses.${value}`) })),
      },
    ];
  }

  private configureTranslations(): void {
    this.actions = [
      { id: 'details', icon: 'visibility', tooltip: this.t('overtimesCatalog.actions.view'), colorClass: 'info', auth: { resource: 'overtimes', action: 'read' }, selectionMode: 'single' },
      { id: 'approve', icon: 'check_circle', tooltip: this.t('overtimesCatalog.actions.approve'), colorClass: 'success', auth: { resource: 'overtimes', action: 'approve' }, selectionMode: 'single', predicate: (selection) => selection[0]?.status === 'pending' },
      { id: 'reject', icon: 'cancel', tooltip: this.t('overtimesCatalog.actions.reject'), colorClass: 'warning', auth: { resource: 'overtimes', action: 'approve' }, selectionMode: 'single', predicate: (selection) => selection[0]?.status === 'pending' },
      { id: 'delete', icon: 'delete', tooltip: this.t('overtimesCatalog.actions.delete'), colorClass: 'danger', auth: { resource: 'overtimes', action: 'delete' }, selectionMode: 'oneOrMore' },
    ];

    this.columnDefinitions = [
      { def: 'select', label: this.t('common.select'), type: 'check', visible: true },
      { def: 'employeeNumber', label: this.t('overtimesCatalog.fields.employeeNumber'), type: 'text', visible: true },
      { def: 'employeeLabel', label: this.t('overtimesCatalog.fields.employee'), type: 'text', visible: true },
      { def: 'companyName', label: this.t('overtimesCatalog.fields.company'), type: 'text', visible: true },
      { def: 'orgUnitName', label: this.t('overtimesCatalog.fields.orgUnit'), type: 'text', visible: true },
      { def: 'workDateLabel', label: this.t('overtimesCatalog.fields.workDate'), type: 'text', visible: true },
      { def: 'detectedMinutesLabel', label: this.t('overtimesCatalog.fields.detectedMinutes'), type: 'text', visible: true },
      { def: 'approvedMinutesLabel', label: this.t('overtimesCatalog.fields.approvedMinutes'), type: 'text', visible: true },
      { def: 'statusLabel', label: this.t('overtimesCatalog.fields.status'), type: 'status', visible: true },
      { def: 'requestedAtLabel', label: this.t('overtimesCatalog.fields.requestedAt'), type: 'text', visible: true },
    ];

    this.tableFilters = this.buildTableFilters();
  }

  private extractList<T>(response: T[] | ApiListResponse<T>): T[] {
    if (Array.isArray(response)) {
      return response;
    }
    return response.items || response.data || response.results || [];
  }

  private flattenOrgUnits(units: OrgUnitRecord[], parentId: string | null = null): OrgUnitRecord[] {
    return units.flatMap((unit) => {
      const normalizedUnit = { ...unit, parentId: unit.parentId ?? parentId };
      return [normalizedUnit, ...this.flattenOrgUnits(unit.children || [], unit.orgUnitId)];
    });
  }

  private getCompanyName(id?: string | null): string {
    if (!id) {
      return this.t('overtimesCatalog.labels.noCompany');
    }
    return this.companies.find((company) => company.companiesRecordId === id)?.name || id;
  }

  private getOrgUnitName(id?: string | null): string {
    if (!id) {
      return this.t('overtimesCatalog.labels.noOrgUnit');
    }
    return this.orgUnits.find((unit) => unit.orgUnitId === id)?.name || id;
  }

  private translateStatus(status?: string | null): string {
    if (!status) {
      return '-';
    }
    const key = `overtimesCatalog.statuses.${status}`;
    const translated = this.t(key);
    return translated !== key ? translated : status;
  }

  private formatDate(value?: string | null): string {
    if (!value) {
      return '-';
    }
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    const locale = this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX';
    return new Intl.DateTimeFormat(locale, { year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
  }

  private formatDateTime(value?: string | null): string {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    const locale = this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX';
    return new Intl.DateTimeFormat(locale, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).format(date);
  }

  private formatMinutes(value?: number | null): string {
    if (value === null || value === undefined) {
      return '-';
    }
    const hours = Math.floor(value / 60);
    const minutes = value % 60;
    if (!hours) {
      return `${minutes}m`;
    }
    return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
  }

  private getActiveCompanyId(): string {
    return (this.activeFilters['company'] as string) || '';
  }

  private getActiveOrgUnitId(): string {
    return (this.activeFilters['orgUnit'] as string) || '';
  }

  private getActiveEmployeeId(): string {
    return (this.activeFilters['employee'] as string) || '';
  }

  private getActiveWorkDate(): string {
    return (this.activeFilters['workDate'] as string) || '';
  }

  private getActiveStatus(): string {
    return (this.activeFilters['status'] as string) || '';
  }

  private hasAccess(action: string): boolean {
    return this.authorizationService.hasAccess({ resource: 'overtimes', action });
  }

  private showNotification(colorName: string, text: string, placementFrom: MatSnackBarVerticalPosition, placementAlign: MatSnackBarHorizontalPosition): void {
    this.snackBar.open(text, '', { duration: 2200, verticalPosition: placementFrom, horizontalPosition: placementAlign, panelClass: colorName });
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }
}
